import { useCallback, useEffect, useState } from 'react';
import { ArrowLeftRight, Loader2, Merge, RefreshCw, Search, Users, X } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useConfirm } from '../../components/shared/ConfirmDialog';
import { useAlerts } from '../../contexts/AlertsContext';
import {
  SuspectedDuplicate,
  SuspectedDuplicateStatus,
  UserSummary,
  duplicatesApi,
} from '../../api/duplicates';
import { InfoCard } from './components/InfoCard';
import { MergeDialog, MergeTarget } from './components/MergeDialog';
import { UserSummaryCard, apiErrorMessage, formatDate, tgHandle, userTitle } from './components/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState, ErrorState, FormField, PageHeader, SegmentedControl, StatusPill, Toolbar } from '@/components/app';

const STATUS_FILTERS: Array<{ value: SuspectedDuplicateStatus; label: string }> = [
  { value: 'open', label: 'Открытые' },
  { value: 'merged', label: 'Склеены' },
  { value: 'dismissed', label: 'Не дубль' },
];

// Тема — через CSS-переменные; пропсы маршрута из App.tsx не используются.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export default function DuplicatesPage(_props: Record<string, unknown>) {
  const [tab, setTab] = useState<'suspected' | 'search'>('suspected');
  const [mergeTarget, setMergeTarget] = useState<MergeTarget | null>(null);
  // Счётчик, по которому вкладки перезагружают данные после склейки
  const [reloadKey, setReloadKey] = useState(0);
  const { reload: reloadAlerts } = useAlerts();

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Дубли аккаунтов"
        subtitle="Склейка дубля, созданного входом через Telegram, с основным аккаунтом человека"
      />

      <InfoCard />

      <Tabs value={tab} onValueChange={(v) => setTab(v as 'suspected' | 'search')}>
        <TabsList className="self-start">
          <TabsTrigger value="suspected">Возможные дубли</TabsTrigger>
          <TabsTrigger value="search">Поиск и ручная склейка</TabsTrigger>
        </TabsList>

        <TabsContent value="suspected" className="mt-4">
          <SuspectedTab reloadKey={reloadKey} onMerge={setMergeTarget} />
        </TabsContent>
        <TabsContent value="search" className="mt-4">
          <SearchTab reloadKey={reloadKey} onMerge={setMergeTarget} />
        </TabsContent>
      </Tabs>

      <MergeDialog
        target={mergeTarget}
        onClose={() => setMergeTarget(null)}
        onMerged={() => {
          setReloadKey((k) => k + 1);
          reloadAlerts();
        }}
      />
    </div>
  );
}

// ─── Вкладка «Возможные дубли» ────────────────────────────────────────────────

interface TabProps {
  reloadKey: number;
  onMerge: (target: MergeTarget) => void;
}

function SuspectedTab({ reloadKey, onMerge }: TabProps) {
  const { reload: reloadAlerts } = useAlerts();
  const confirm = useConfirm();
  const [status, setStatus] = useState<SuspectedDuplicateStatus>('open');
  const [items, setItems] = useState<SuspectedDuplicate[]>([]);
  const [loading, setLoading] = useState(true);
  const [dismissingId, setDismissingId] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setItems(await duplicatesApi.listSuspected(status));
    } catch (err) {
      setLoadError(apiErrorMessage(err, 'Не удалось загрузить возможные дубли'));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    load();
  }, [load, reloadKey]);

  const handleDismiss = async (item: SuspectedDuplicate) => {
    const ok = await confirm({
      title: 'Отметить «Не дубль»?',
      description: `Пара «${userTitle(item.newUser)}» ↔ «${userTitle(item.previousUser)}» уйдёт из открытых. Аккаунты не меняются.`,
      confirmLabel: 'Не дубль',
    });
    if (!ok) return;
    setDismissingId(item.id);
    try {
      await duplicatesApi.dismiss(item.id);
      toast.success('Отмечено: не дубль');
      setItems((prev) => prev.filter((i) => i.id !== item.id));
      reloadAlerts();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось отметить'));
    } finally {
      setDismissingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl ariaLabel="Статус пары" value={status} onChange={setStatus} options={STATUS_FILTERS} />
        <Button size="sm" variant="ghost" onClick={load} disabled={loading} className="ml-auto text-text-2">
          <RefreshCw className={cn(loading && 'animate-spin')} />
          Обновить
        </Button>
      </div>

      {loading ? (
        <div className="flex flex-col gap-4" aria-busy="true" aria-label="Загрузка">
          {[0, 1].map((i) => (
            <Card key={i} className="flex flex-col gap-3 p-[18px]">
              <Skeleton className="h-4 w-40" />
              <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                <Skeleton className="h-48" />
                <Skeleton className="h-48" />
              </div>
            </Card>
          ))}
        </div>
      ) : loadError ? (
        <Card>
          <ErrorState message={loadError} onRetry={load} />
        </Card>
      ) : items.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Users />}
            title={status === 'open' ? 'Открытых возможных дублей нет' : 'Список пуст'}
          />
        </Card>
      ) : (
        items.map((item) => (
          <Card key={item.id} className="flex flex-col gap-3 p-[18px]">
            <p className="text-xs text-text-2">
              Замечено: <span className="tabular-nums">{formatDate(item.detectedAt)}</span>
            </p>
            <div className="grid grid-cols-1 items-stretch gap-3 lg:grid-cols-[1fr_auto_1fr]">
              <UserSummaryCard user={item.newUser} role="Дубль (Telegram)" tone="duplicate" />
              <div className="flex items-center justify-center">
                <ArrowLeftRight className="size-5 rotate-90 text-text-2 lg:rotate-0" aria-hidden />
              </div>
              <UserSummaryCard user={item.previousUser} role="Основной" tone="main" />
            </div>
            {item.status === 'open' && (
              <div className="flex flex-wrap justify-end gap-2">
                <Button variant="secondary" onClick={() => handleDismiss(item)} disabled={dismissingId === item.id}>
                  {dismissingId === item.id ? <Loader2 className="animate-spin" /> : <X />}
                  Не дубль
                </Button>
                <Button onClick={() => onMerge({ duplicateId: item.newUser.id, mainId: item.previousUser.id })}>
                  <Merge />
                  Склеить…
                </Button>
              </div>
            )}
          </Card>
        ))
      )}
    </div>
  );
}

// ─── Вкладка «Поиск и ручная склейка» ─────────────────────────────────────────

function SearchTab({ reloadKey, onMerge }: TabProps) {
  const [query, setQuery] = useState('');
  const [lastQuery, setLastQuery] = useState<string | null>(null);
  const [results, setResults] = useState<UserSummary[]>([]);
  const [searching, setSearching] = useState(false);
  const [duplicate, setDuplicate] = useState<UserSummary | null>(null);
  const [main, setMain] = useState<UserSummary | null>(null);

  const runSearch = useCallback(async (q: string) => {
    const trimmed = q.trim();
    if (trimmed.length < 2) {
      toast.error('Введите хотя бы 2 символа');
      return;
    }
    setSearching(true);
    try {
      setResults(await duplicatesApi.searchUsers(trimmed));
      setLastQuery(trimmed);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Не удалось выполнить поиск'));
    } finally {
      setSearching(false);
    }
  }, []);

  // После успешной склейки сбрасываем выбор и обновляем выдачу (у дубля сменился статус)
  useEffect(() => {
    if (reloadKey === 0) return;
    setDuplicate(null);
    setMain(null);
    if (lastQuery) runSearch(lastQuery);
  }, [reloadKey]);

  const sameUser = !!duplicate && !!main && duplicate.id === main.id;
  const noTelegram = !!duplicate && !duplicate.telegramId;

  return (
    <div className="flex flex-col gap-4">
      <Card className="flex flex-col gap-3 p-[18px]">
        <form
          className="flex flex-col gap-2 sm:flex-row sm:items-end"
          onSubmit={(e) => {
            e.preventDefault();
            runSearch(query);
          }}
        >
          <FormField
            label="Поиск аккаунта"
            className="flex-1"
            hint="Номер можно вводить частью цифр (например, 901234567). Найдите оба аккаунта человека и отметьте, какой из них дубль (с Telegram), а какой — основной."
          >
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Номер, @telegram, telegram id, email или username"
            />
          </FormField>
          <Button type="submit" disabled={searching} className="sm:mb-[22px]">
            {searching ? <Loader2 className="animate-spin" /> : <Search />}
            Найти
          </Button>
        </form>
      </Card>

      {/* Выбранная пара */}
      <Card className="flex flex-col gap-3 p-[18px]">
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          {duplicate ? (
            <UserSummaryCard
              user={duplicate}
              role="Дубль (Telegram)"
              tone="duplicate"
              footer={
                <Button size="sm" variant="ghost" onClick={() => setDuplicate(null)} className="text-text-2">
                  <X /> Убрать
                </Button>
              }
            />
          ) : (
            <EmptySlot text="Дубль (Telegram) не выбран" />
          )}
          {main ? (
            <UserSummaryCard
              user={main}
              role="Основной"
              tone="main"
              footer={
                <Button size="sm" variant="ghost" onClick={() => setMain(null)} className="text-text-2">
                  <X /> Убрать
                </Button>
              }
            />
          ) : (
            <EmptySlot text="Основной аккаунт не выбран" />
          )}
        </div>
        {sameUser && (
          <p role="alert" className="text-sm font-semibold text-neg">
            Дубль и основной — один и тот же аккаунт.
          </p>
        )}
        {noTelegram && (
          <p className="text-sm font-semibold text-warn">У выбранного дубля нет входа через Telegram — переносить нечего.</p>
        )}
        <div className="flex justify-end">
          <Button
            disabled={!duplicate || !main || sameUser}
            onClick={() => duplicate && main && onMerge({ duplicateId: duplicate.id, mainId: main.id })}
          >
            <Merge />
            Склеить…
          </Button>
        </div>
      </Card>

      {/* Результаты поиска */}
      {lastQuery !== null && (
        <Card className="overflow-hidden">
          <Toolbar
            title={<>Найдено <span className="tabular-nums">{results.length}</span></>}
            end={results.length >= 20 ? 'Показаны первые 20 — уточните запрос' : undefined}
          />
          {results.length === 0 ? (
            <EmptyState title="Ничего не найдено" description="Проверьте запрос: номер, @telegram, email или username." />
          ) : (
            <ul className="m-0 list-none p-0">
              {results.map((u) => {
                const unavailable = u.isDeleted || !!u.mergedIntoUserId;
                const isDup = duplicate?.id === u.id;
                const isMain = main?.id === u.id;
                return (
                  <li
                    key={u.id}
                    className={cn(
                      'flex flex-col gap-3 border-t border-border-soft px-[18px] py-3 md:flex-row md:items-center',
                      (isDup || isMain) && 'bg-accent-soft',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="flex items-center gap-2 truncate font-semibold text-text">
                        {userTitle(u)}
                        {u.mergedIntoUserId && <StatusPill size="sm" tone="neutral">склеен</StatusPill>}
                        {u.isDeleted && <StatusPill size="sm" tone="neg">удалён</StatusPill>}
                      </p>
                      <p className="break-all text-xs text-text-2">
                        {[
                          u.email,
                          u.phone,
                          u.telegramId ? `TG ${tgHandle(u)}` : null,
                          `создан ${formatDate(u.createdAt)}`,
                          u.isSubscribed ? `премиум до ${formatDate(u.subscribedEnd)}` : null,
                          u.hasPayments ? 'есть оплаты' : null,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                    <div className="flex shrink-0 gap-2">
                      <Button
                        size="sm"
                        variant={isDup ? 'inverse' : 'secondary'}
                        aria-pressed={isDup}
                        disabled={unavailable}
                        onClick={() => setDuplicate(u)}
                      >
                        Это дубль
                      </Button>
                      <Button
                        size="sm"
                        variant={isMain ? 'default' : 'secondary'}
                        aria-pressed={isMain}
                        disabled={unavailable}
                        onClick={() => setMain(u)}
                      >
                        Это основной
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      )}
    </div>
  );
}

function EmptySlot({ text }: { text: string }) {
  return (
    <div className="flex min-h-[120px] items-center justify-center rounded-xl border-2 border-dashed border-border p-6 text-sm text-text-2">
      {text}
    </div>
  );
}
